import Konva from "konva";
import { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";
import { Category } from "@/types/models";
import {
  canvasTextFontFamily,
  canvasTextFontSize,
  charLimit,
  expandedTextRectWidth,
  labelRectMaxWidth,
  labelRectMinHeight,
  labelRectMinWidth,
  labelRectPadding,
  textRectPadding,
  trimmedTextRectWidth,
  pageBadgeHeadroom,
} from "./consts";
import { Rectangle, RectangleGeometry } from "@/types/canvasTypes";

/**
 * Pull a short, readable label out of an arbitrary text blob. Used as
 * the default rectangle body when the user drops an annotation onto
 * the canvas (and dismisses the label prompt) or directly creates a
 * text rect. Caps at the first ~6 whitespace-delimited words and ~32
 * characters; trims trailing punctuation so we don't get labels like
 * "On the question of,".
 */
export const autoLabel = (text: string, maxWords = 6, maxChars = 32): string => {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/).slice(0, maxWords).join(" ");
  let candidate = words;
  if (candidate.length > maxChars) {
    // Truncate at the last word boundary that still fits.
    const slice = candidate.slice(0, maxChars);
    const lastSpace = slice.lastIndexOf(" ");
    candidate = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
  }
  return candidate.replace(/[\s.,;:!?\-—–]+$/, "");
};

/**
 * Size for a rectangle rendered in label mode (collapsed). Wraps long
 * labels within labelRectMaxWidth and pads aggressively so single-word
 * labels still feel like proper nodes rather than typographic ribbons,
 * which is what mind-map / Figma-style boards look like.
 */
export const calculateLabelSize = (label: string) => {
  const textComponent = new Konva.Text({
    text: label,
    padding: labelRectPadding,
    fontFamily: canvasTextFontFamily,
    fontSize: canvasTextFontSize,
    width: labelRectMaxWidth,
    wrap: "word",
    align: "center",
  });

  const natural = textComponent.size();
  return {
    width: Math.max(labelRectMinWidth, Math.min(natural.width, labelRectMaxWidth)),
    height: Math.max(labelRectMinHeight, natural.height),
  };
};

/**
 * The string a collapsed text rect displays: the explicit tag, or an
 * auto-derived label — with a trailing ellipsis whenever the full text
 * holds more than the label shows, so condensed nodes signal that
 * expanding them reveals more. Sizing and rendering must both use this
 * so the measured string is exactly the displayed one.
 */
export const collapsedDisplayBody = (
  tag: string | undefined,
  text: string | undefined
): string => {
  const body = tag || autoLabel(text || "");
  // Compare with trailing punctuation stripped from both sides:
  // autoLabel trims it from labels, and a period's worth of hidden
  // "content" doesn't warrant an ellipsis.
  const norm = (v: string) => v.trim().replace(/[\s.,;:!?\-—–]+$/, "");
  const hasMore = !!text && norm(text) !== norm(body);
  return hasMore ? `${body}…` : body;
};

/**
 * THE canonical size for a collapsed rect. Every path that sizes a
 * collapsed node (creation on drop, expand/collapse toggle, text edit)
 * must go through this so they can never disagree: tight label fit,
 * clamped to the label minimums, plus one badge-row of headroom when
 * the rect shows a page-provenance badge.
 */
export const collapsedRectSize = (
  body: string,
  hasBadge: boolean
): { width: number; height: number } => {
  const size = calculateLabelSize(body);
  if (hasBadge) size.height += pageBadgeHeadroom;
  return size;
};

export const calculateTrimmedTextSize = (text: string) => {
  const textComponent = new Konva.Text({
    text: text.slice(0, charLimit),
    padding: textRectPadding,
    fontFamily: canvasTextFontFamily,
    fontSize: canvasTextFontSize,
  });

  if (textComponent.size().width > trimmedTextRectWidth) {
    textComponent.width(trimmedTextRectWidth);
  }

  return textComponent.size();
};

export const calculateExpandedTextSize = (text: string) => {
  const textComponent = new Konva.Text({
    text,
    padding: textRectPadding,
    fontFamily: canvasTextFontFamily,
    fontSize: canvasTextFontSize,
  });

  if (textComponent.size().width > expandedTextRectWidth) {
    textComponent.width(expandedTextRectWidth);
  }

  return textComponent.size();
};

export const rectsHaveIntersection = (
  A: RectangleGeometry,
  B: RectangleGeometry
) => {
  return (
    A.x + A.width > B.x &&
    B.x + B.width > A.x &&
    A.y + A.height > B.y &&
    B.y + B.height > A.y
  );
};

// Sort highlights by their location in the document: page, then top-to-
// bottom, then left-to-right. Positions store raw viewport pixels captured
// at whatever zoom was active, alongside the viewport width/height at
// capture (see the highlighter's viewportToScaled) — raw y1/x1 values from
// different zoom levels are on different scales, so we normalize to a
// fraction of the page (y1/height), exactly how scaledToViewport re-renders
// them. The sidebar's category filter is applied per-item at render, so the
// list order stays pure document order regardless of filter.
export const sortHighlightsByLocation = function (array: Array<IHighlight>) {
  const pageOf = (h: IHighlight) =>
    h.position?.pageNumber ?? h.position?.boundingRect?.pageNumber ?? 0;
  const normY = (h: IHighlight) => {
    const br = h.position?.boundingRect;
    if (!br || typeof br.y1 !== "number") return 0;
    return br.height ? br.y1 / br.height : br.y1;
  };
  const normX = (h: IHighlight) => {
    const br = h.position?.boundingRect;
    if (!br || typeof br.x1 !== "number") return 0;
    return br.width ? br.x1 / br.width : br.x1;
  };

  const copy = array.slice();
  copy.sort((a, b) => {
    if (pageOf(a) !== pageOf(b)) return pageOf(a) - pageOf(b);
    if (normY(a) !== normY(b)) return normY(a) - normY(b);
    return normX(a) - normX(b);
  });
  return copy;
};

// We need to add 5 pixels around the rect so that arrows don't touch boxes.
export const addMargin = (
  { x, y, width, height }: RectangleGeometry,
  margin: number
): RectangleGeometry => {
  return {
    x: x - margin,
    y: y - margin,
    width: width + 2 * margin,
    height: height + 2 * margin,
  };
};

/**
 * Konva dash state for a credence value (0-100). At 100 the stroke is
 * solid (dashEnabled false). Below 100 the dash pattern interpolates
 * from barely-broken toward a sparse dash at very low credence.
 * Undefined credence (pre-dating the field) is treated as 100.
 * Shared by arrows (inference credence) and rectangles (statement
 * credence) so both speak the same visual language.
 */
export const dashStateForCredence = (
  credence: number | undefined
): { enabled: boolean; pattern: [number, number] } => {
  const c = credence ?? 100;
  if (c >= 100) return { enabled: false, pattern: [8, 4] };
  const t = (100 - c) / 100; // 0 = full credence, 1 = none
  // Dash shrinks as credence drops; gap grows.
  const dash = Math.max(2, 12 - t * 10);
  const gap = 4 + t * 14;
  return { enabled: true, pattern: [dash, gap] };
};
